import React, { ReactNode } from "react";
import "i18next";
import { Resource, i18n as I18nInstance } from "i18next";
import HttpBackend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import i18n from "i18next";
import { I18nextProvider, initReactI18next } from "react-i18next";

import { getResourcesWithFallbacks } from "./browser-resources";
import { config } from "./config";
import { formatLocale } from "./utils";
import { getNamespaceForBuildType } from "./resources";

let baseI18nInstance: I18nInstance | null = null;
let initPromise: Promise<I18nInstance> | null = null;
const localeInstances = new Map<string, I18nInstance>();

const addResourceBundles = (instance: I18nInstance, resources: Resource) => {
  Object.entries(resources).forEach(([lngKey, namespaces]) => {
    const namespaceEntries = namespaces as Record<string, any>;
    Object.entries(namespaceEntries).forEach(([namespace, resource]) => {
      instance.addResourceBundle(lngKey, namespace, resource, true, true);
    });
  });
};

const preloadResources = async (
  lng: string,
  namespace: string,
): Promise<{ formattedLng: string; resources: Resource }> => {
  const formattedLng = formatLocale(lng);
  const resources = await getResourcesWithFallbacks(formattedLng, namespace);
  return { formattedLng, resources };
};

export const configureBrowserI18n = async (resources: Resource, lng: string, namespace: string) => {
  if (!i18n.isInitialized) {
    await i18n
      .use(initReactI18next)
      .use(HttpBackend) // Load translations using http-backend
      .use(LanguageDetector) // Detect user language
      .init(
        config(lng, resources, {
          load: "all",
          debug: false,
          defaultNS: namespace,
          ns: namespace,
          backend: {
            loadPath: `/language/{{lng}}/${namespace}.json`,
            addPath: `/language/{{lng}}/${namespace}.json`,
          },
        }),
      );

    // Expose i18n instance to window for use in wrapPageElement
    if (typeof window !== "undefined") {
      (window as any).i18n = i18n;
    }
  } else {
    await i18n.changeLanguage(lng);
  }

  return i18n;
};

export const initBrowserI18n = async (lng: string, namespace: string): Promise<I18nInstance> => {
  // Return existing promise if initialization is in progress
  if (initPromise) {
    return initPromise;
  }

  // If already initialized, load the resources for the new language and switch
  if (baseI18nInstance) {
    initPromise = (async () => {
      const { formattedLng, resources } = await preloadResources(lng, namespace);
      addResourceBundles(baseI18nInstance!, resources);
      localeInstances.delete(formattedLng);
      await baseI18nInstance!.changeLanguage(formattedLng);
      return baseI18nInstance!;
    })();

    try {
      return await initPromise;
    } finally {
      initPromise = null;
    }
  }

  // Initialize for the first time
  initPromise = (async () => {
    const { formattedLng, resources } = await preloadResources(lng, namespace);
    baseI18nInstance = await configureBrowserI18n(resources, formattedLng, namespace);
    return baseI18nInstance;
  })();

  try {
    const instance = await initPromise;
    return instance;
  } finally {
    initPromise = null; // Reset for potential future re-initialization (HMR)
  }
};

// Get or create a cached i18n instance for a specific locale
// This ensures we use the same instance structure for SSR and hydration
const getLocaleInstance = (locale: string): I18nInstance => {
  const formattedLocale = formatLocale(locale);

  // Return cached instance if it exists
  if (localeInstances.has(formattedLocale)) {
    return localeInstances.get(formattedLocale)!;
  }

  // Create a new cloned instance for this locale
  // Note: This clones the configuration and shares the resource store with base instance
  const instance = baseI18nInstance
    ? baseI18nInstance.cloneInstance({ lng: formattedLocale })
    : i18n.cloneInstance({ lng: formattedLocale });

  // Cache and return
  localeInstances.set(formattedLocale, instance);
  return instance;
};

// Pre-create and cache a locale instance (call in onClientEntry)
export const getLocaleInstanceSync = (locale: string): I18nInstance => {
  return getLocaleInstance(locale);
};

/**
 * The browser-side i18n provider `gatsby-browser`'s `wrapPageElement` wraps every page in.
 *
 * Deliberately one module-level component rather than something built per call: `wrapPageElement`
 * runs on every render of the page element, and React identifies a component by function
 * reference. Handing it a fresh function each time made it a different element type, so React
 * unmounted the whole tree below — layout, header, footer — and mounted a new one on every router
 * update, including the hash change from an in-page anchor. That rebuilt the hydrated page from
 * scratch and flashed the header each time.
 */
export const I18nextBrowserProvider = ({ children, locale }: { children: ReactNode; locale?: string }) => {
  if (!locale) {
    throw new Error("locale prop is required for I18nextBrowserProvider");
  }

  const instance = getLocaleInstance(locale);

  return <I18nextProvider i18n={instance}>{children}</I18nextProvider>;
};
